const React = require('react')
const { useState, useEffect } = require('react')
const { useParams, useHistory } = require('react-router-dom')
const { Container, Col, Row, UncontrolledTooltip, Carousel, CarouselItem, CarouselControl } = require('reactstrap')
const { useFormatBytes, useBlockchainData, useResponsive, BREAKPOINTS } = require('@hooks')
const { useNetworkConfig } = require('@contexts')
const { LoadingSpinner, CopyButton, CustomCard } = require('@components')
const { TableHeader, TableFooter } = require('@components/table')
const { NFTActivityTable } = require('./activityTable.nft')
const { NFTOpinionCollectionLogs } = require('./opinionCollectionLogs.nft')

require('@styles/pages/nft/index.css')

const NFTProperties = ({ data }) => {
    const [activeIndex, setActiveIndex] = useState(0)
    const next = () => setActiveIndex(prev => (prev === data.length - 1 ? 0 : prev + 1))
    const previous = () => setActiveIndex(prev => (prev === 0 ? data.length - 1 : prev - 1))

    return (
        <Carousel
            activeIndex={activeIndex}
            next={next}
            previous={previous}
            style={{ padding: data.length > 1 ? '0px 30px' : '0px' }}
        >
            {data.map((item, index) => (
                <CarouselItem key={index}>
                    {Object.entries(item).map(([key, value], itemIndex) => (
                        <div key={key} style={{ marginTop: itemIndex === 0 ? '0px' : '8px' }}>
                            <span style={{ fontWeight: 'bold' }}>{key}:</span> {value}
                        </div>
                    ))}
                </CarouselItem>
            ))}
            {data.length > 1 && (
                <>
                    <CarouselControl
                        direction='prev'
                        directionText='Previous'
                        onClickHandler={previous}
                        className='carousel-control-custom'
                    />
                    <CarouselControl
                        direction='next'
                        directionText='Next'
                        onClickHandler={next}
                        className='carousel-control-custom'
                    />
                </>
            )}
        </Carousel>
    )
}

const NFT = () => {
    const { address, tokenId } = useParams()
    const history = useHistory()
    const { network } = useNetworkConfig()
    const { getNftOverview, getTokenTransfers, getApplications, getLogsOpinionCollection } = useBlockchainData(
        network.name
    )

    const isMobile = useResponsive(BREAKPOINTS.small)

    const params = new URLSearchParams(location.search)

    const [page, setPage] = useState(Number(params.get('p')) || 1)
    const [limit, setLimit] = useState(Number(params.get('ps')) || 25)

    const [logsPage, setLogsPage] = useState(Number(params.get('lp')) || 1)
    const [logsLimit, setLogsLimit] = useState(Number(params.get('lps')) || 25)

    const formatBytes = useFormatBytes()
    const [loading, setLoading] = useState(false)
    const [nftData, setNftData] = useState(null)
    const [tokenTransfersData, setTokenTransfersData] = useState(null)
    const [applicationsData, setApplicationsData] = useState(null)
    const [subData, setSubData] = useState(null)
    const [nftDetails, setNftDetails] = useState([])

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const pageParam = Number(params.get('p'))
        const limitParam = Number(params.get('ps'))
        const logsPageParam = Number(params.get('lp'))
        const logsLimitParam = Number(params.get('lps'))

        if (pageParam && pageParam > 0 && pageParam !== page) {
            setPage(pageParam)
        }

        if (limitParam && limitParam > 0 && limitParam !== limit) {
            setLimit(limitParam)
        }

        if (logsPageParam && logsPageParam > 0 && logsPageParam !== logsPage) {
            setPage(logsPageParam)
        }

        if (logsLimitParam && logsLimitParam > 0 && logsLimitParam !== logsLimit) {
            setLimit(logsLimitParam)
        }
    }, [location.search])

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)

            try {
                const [nftOverviewResponse, tokenTransfersResponse, applicationsResponse] = await Promise.all([
                    getNftOverview(address, tokenId),
                    getTokenTransfers(page, limit, { address: address, userAddress: tokenId }),
                    getApplications(address)
                ])

                if (nftOverviewResponse && tokenTransfersResponse && applicationsResponse) {
                    setNftData(nftOverviewResponse)
                    setTokenTransfersData(tokenTransfersResponse)
                    setApplicationsData(applicationsResponse)

                    if (applicationsResponse.type == 'OPINION_COLLECTION_ADDRESS') {
                        const [logsResponse] = await Promise.all([
                            getLogsOpinionCollection(logsPage, logsLimit, applicationsResponse.subgraph, tokenId)
                        ])

                        setSubData(logsResponse)
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error)
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [network, page, limit, address, tokenId])

    useEffect(() => {
        if (!nftData || !applicationsData) return

        const fields = {
            owner: {
                id: 'owner',
                label: 'Owner',
                value: (
                    <>
                        <span
                            id='owner'
                            className={`${network.name}-text-color cursor-pointer`}
                            onClick={() => {
                                history.push(`/${network.name}/address/${nftData.tokenOwner.address}`)
                            }}
                        >
                            {formatBytes(nftData.tokenOwner.address, 42, 0, 0)}
                        </span>
                        <UncontrolledTooltip placement='top' target='owner'>
                            {nftData.tokenOwner.address}
                        </UncontrolledTooltip>
                        &nbsp;
                        <CopyButton id={`copy-owner`} data={nftData.tokenOwner.address} text={'Copy Address'} />
                    </>
                ),
                tooltip: 'Current owner of this NFT.'
            },
            contractAddress: {
                id: 'contractAddress',
                label: 'Contract Address',
                value: (
                    <>
                        <span
                            id='contractAddress'
                            className={`${network.name}-text-color cursor-pointer`}
                            onClick={() => {
                                history.push(`/${network.name}/address/${address}`)
                            }}
                        >
                            {formatBytes(address, 42, 0, 0)}
                        </span>
                        <UncontrolledTooltip placement='top' target='contractAddress'>
                            {address}
                        </UncontrolledTooltip>
                        &nbsp;
                        <CopyButton id={`copy-contractAddress`} data={address} text={'Copy Address'} />
                    </>
                ),
                tooltip: 'Address of this NFT contract.'
            },
            creator: {
                id: 'creator',
                label: 'Creator',
                value: (
                    <>
                        <span
                            id='creator'
                            className={`${network.name}-text-color cursor-pointer`}
                            onClick={() => {
                                history.push(`/${network.name}/address/${nftData.tokenCreator.address}`)
                            }}
                        >
                            {formatBytes(nftData.tokenCreator.address, 42, 0, 0)}
                        </span>
                        <UncontrolledTooltip placement='top' target='creator'>
                            {nftData.tokenCreator.address}
                        </UncontrolledTooltip>
                        &nbsp;
                        <CopyButton id={`copy-creator`} data={nftData.tokenCreator.address} text={'Copy Address'} />
                    </>
                ),
                tooltip: 'Address that deployed this NFT contract.'
            },
            tokenId: {
                id: 'tokenId',
                label: 'Token ID',
                value: (
                    <>
                        {tokenId}
                        &nbsp;
                        <CopyButton id={`copy-tokenId`} data={tokenId} text={'Copy Token ID to clipboard '} />
                    </>
                ),
                tooltip: "This NFT's unique token ID."
            },
            tokenStandard: {
                id: 'tokenStandard',
                label: 'Token Standard',
                value: nftData.tokenType,
                tooltip: 'The standard followed by this NFT.'
            },
            tokenUri: {
                id: 'tokenUri',
                label: 'Token URI',
                value: nftData.metadata.uri,
                tooltip: 'Token URI of this NFT.'
            },
            tokenProperties: {
                id: 'tokenProperties',
                label: 'Properties',
                value: <NFTProperties data={nftData.properties} />,
                tooltip: 'Properties of this NFT.'
            }
        }

        const nftDetailsOrder = [
            'owner',
            'contractAddress',
            'creator',
            'tokenId',
            'tokenStandard',
            'tokenUri',
            applicationsData.type == 'OPINION_COLLECTION_ADDRESS' ? 'tokenProperties' : null
        ].filter(Boolean)
        setNftDetails(nftDetailsOrder.map(key => fields[key]))
    }, [nftData, applicationsData])

    return (
        <div className='page'>
            <Container>
                {loading ? (
                    <div className='d-flex justify-content-center align-items-center' style={{ height: '300px' }}>
                        <LoadingSpinner />
                    </div>
                ) : (
                    <>
                        <Row>
                            <Col className='network-table' md={4}>
                                <CustomCard className={`box table-section`} style={{ margin: '0 15px 0 0' }}>
                                    <div
                                        className='nft-image-container'
                                        style={{ backgroundColor: 'var(--dropdown-background)' }}
                                    >
                                        <img src={nftData?.tokenImage} alt={nftData?.name} className='nft-image' />
                                    </div>
                                </CustomCard>
                            </Col>
                            <Col className='network-table' md={8}>
                                <div style={{ marginLeft: '15px' }}>
                                    <div>
                                        <span id='nft-name' style={{ fontWeight: 'bold', fontSize: '20px' }}>
                                            {nftData?.tokenName}
                                        </span>
                                    </div>
                                    <UncontrolledTooltip placement='top' target='nft-name'>
                                        {nftData?.tokenName} #{tokenId}
                                    </UncontrolledTooltip>
                                    <div>
                                        <span
                                            id='nft-symbol'
                                            style={{ fontSize: '14px', marginTop: '8px' }}
                                            className={`${network.name}-text-color cursor-pointer`}
                                            onClick={() => history.push(`/${network.name}/token/${address}`)}
                                        >
                                            {nftData?.tokenSymbol}
                                        </span>
                                    </div>
                                    <UncontrolledTooltip placement='top' target='nft-symbol'>
                                        {nftData?.tokenSymbol}
                                    </UncontrolledTooltip>
                                    <CustomCard className={`box table-section`}>
                                        {nftDetails?.map(item => (
                                            <Row className='table-info-row' noGutters>
                                                <Col xs={12} sm={4} className='d-flex align-items-start'>
                                                    <span className='info-icon-wrapper' id={item.id}>
                                                        <i className='fas fa-question-circle info-icon' />
                                                    </span>
                                                    <span className='info-label'>{item.label}:</span>
                                                </Col>
                                                <UncontrolledTooltip placement='top' target={item.id}>
                                                    {item.tooltip}
                                                </UncontrolledTooltip>
                                                <Col
                                                    xs={12}
                                                    sm={8}
                                                    className={`d-flex align-items-center ${isMobile && 'mt-2'}`}
                                                    style={{ flexWrap: 'wrap', wordBreak: 'break-all' }}
                                                >
                                                    <span className='info-value' style={{ width: '100%' }}>
                                                        {item.value}
                                                    </span>
                                                </Col>
                                            </Row>
                                        ))}
                                    </CustomCard>
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <div className='box table-section'>
                                <TableHeader
                                    title={'Item Activity'}
                                    subtitle={
                                        tokenTransfersData?.totalItems > 0
                                            ? `A total of ${tokenTransfersData.totalItems} record${
                                                  tokenTransfersData.totalItems === 1 ? '' : 's'
                                              } found`
                                            : 'No record found'
                                    }
                                    page={page}
                                    limit={limit}
                                    totalPages={tokenTransfersData?.totalPages}
                                />
                                <NFTActivityTable data={tokenTransfersData?.items} />
                                {tokenTransfersData?.totalItems > 0 && (
                                    <TableFooter
                                        page={page}
                                        limit={limit}
                                        totalPages={tokenTransfersData?.totalPages}
                                    />
                                )}
                            </div>
                        </Row>
                        <>
                            {applicationsData?.type == 'OPINION_COLLECTION_ADDRESS' && (
                                <Row>
                                    <div className='box table-section'>
                                        <TableHeader
                                            title={'Item Logs'}
                                            subtitle={
                                                subData?.totalItems > 0
                                                    ? `A total of ${subData.totalItems} record${
                                                          subData.totalItems === 1 ? '' : 's'
                                                      } found`
                                                    : 'No record found'
                                            }
                                            page={page}
                                            limit={limit}
                                            totalPages={subData?.totalPages}
                                        />
                                        <NFTOpinionCollectionLogs data={subData.items} />
                                        {subData?.totalItems > 0 && (
                                            <TableFooter page={page} limit={limit} totalPages={subData?.totalPages} />
                                        )}
                                    </div>
                                </Row>
                            )}
                        </>
                    </>
                )}
            </Container>
        </div>
    )
}

module.exports = NFT
